import { useState } from 'react';
import { Recipe } from '@/types/api';
import MealPlannerModal from '@/components/meal-planner/MealPlannerModal';
import { useEnsureMealPlan } from '@/hooks';
import { OptimizedImage } from '@/components/ui/optimized-image';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { 
  Heart, 
  Clock, 
  Users, 
  Plus,
  ArrowLeft,
  Star,
  StarIcon,
  ChefHat,
  Globe,
  Utensils
} from 'lucide-react';
import { cn } from '@/lib/utils';

interface RecipeDetailProps {
  recipe: Recipe;
  onBack?: () => void;
  onFavoriteToggle?: (recipe: Recipe) => void;
  onAddToMealPlan?: (recipe: Recipe) => void;
  onRatingChange?: (recipe: Recipe, rating: number) => void;
  isFavorite?: boolean;
  userRating?: number;
  averageRating?: number;
  totalRatings?: number;
  className?: string;
}

export function RecipeDetail({
  recipe,
  onBack,
  onFavoriteToggle,
  onAddToMealPlan,
  onRatingChange,
  isFavorite = false,
  userRating = 0,
  averageRating = 0,
  totalRatings = 0,
  className
}: RecipeDetailProps) {
  const [mealPlannerOpen, setMealPlannerOpen] = useState(false);
  const ensureMealPlan = useEnsureMealPlan();


  const handleFavoriteClick = () => {
    onFavoriteToggle?.(recipe);
  };

  const handleAddToMealPlan = () => {
    if (onAddToMealPlan) {
      onAddToMealPlan(recipe);
    } else {
      setMealPlannerOpen(true);
    }
  };

  const handleConfirmSchedule = async ({ dateISO, service }: { dateISO: string; service: 'comida' | 'cena' }) => {
    try {
      await ensureMealPlan.mutateAsync({
        recipeId: recipe.id,
        dateISO,
        service,
      });
    } catch (error) {
      console.error('Failed to schedule meal:', error);
    } finally {
      setMealPlannerOpen(false);
    }
  };

  const handleRatingClick = (rating: number) => {
    onRatingChange?.(recipe, rating);
  };

  // Parse ingredients from the recipe
  const ingredients = recipe.ingredients || [];
  
  // Parse instructions - split by periods and filter empty
  const instructions = recipe.instructions
    ?.split(/[.\n]/)
    .filter(step => step.trim().length > 10)
    .map(step => step.trim()) || [];

  return (
    <div className={cn("max-w-4xl mx-auto", className)}>
      {/* Header with Back Button */}
      {onBack && (
        <div className="flex items-center gap-4 mb-6">
          <Button 
            variant="outline" 
            size="sm"
            onClick={onBack}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Volver
          </Button>
        </div>
      )}

      {/* Recipe Hero Section */}
      <div className="bg-white rounded-lg shadow-sm overflow-hidden mb-6">
        {/* Recipe Title - Sobre la imagen */}
        <div className="p-4 sm:p-6 border-b border-gray-100">
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 leading-tight">
            {recipe.title}
          </h1>
        </div>

        {/* Recipe Image */}
        <div className="relative aspect-[16/9] md:aspect-[2/1]">
          <OptimizedImage
            recipe={recipe}
            alt={recipe.title}
            className="w-full h-full object-cover"
            fallbackClassName="w-full h-full bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center"
          />
        </div>

        {/* Footer con datos y botones */}
        <div className="p-4 sm:p-6 bg-gray-50 border-t border-gray-100">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            {/* Meta Info */}
            <div className="flex items-center gap-4 text-gray-600">
              {recipe.timeMinutes && (
                <div className="flex items-center gap-1">
                  <Clock className="h-4 w-4" />
                  <span className="text-sm font-medium">{recipe.timeMinutes}min</span>
                </div>
              )}
              <div className="flex items-center gap-1">
                <Users className="h-4 w-4" />
                <span className="text-sm font-medium">{recipe.servings} personas</span>
              </div>
              {recipe.source && (
                <div className="flex items-center gap-1">
                  <Globe className="h-4 w-4" />
                  <span className="text-sm font-medium">{recipe.source}</span>
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex items-center gap-3">
              {/* Favorite Button */}
              <Button
                variant="outline"
                size="sm"
                className={cn(
                  "h-10 px-4 touch-manipulation active:scale-95 transition-transform duration-150",
                  isFavorite && "border-red-200 bg-red-50 text-red-600"
                )}
                onClick={handleFavoriteClick}
              >
                <Heart 
                  className={cn(
                    "h-4 w-4 mr-2", 
                    isFavorite && "fill-current text-red-500"
                  )} 
                />
                {isFavorite ? 'Favorito' : 'Guardar'}
              </Button>

              {/* Add to Meal Plan Button */}
              <Button
                size="sm"
                className="h-10 px-4 bg-primary hover:bg-primary/90 text-white
                           touch-manipulation active:scale-95 transition-transform duration-150"
                onClick={handleAddToMealPlan}
                disabled={ensureMealPlan.isPending}
              >
                {ensureMealPlan.isPending ? (
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 border border-white/30 border-t-white rounded-full animate-spin" />
                    Añadiendo...
                  </div>
                ) : (
                  <>
                    <Plus className="h-4 w-4 mr-2" />
                    Agregar al Plan
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Main Content */}
        <div className="lg:col-span-2 space-y-6">

          {/* Instructions */}
          {instructions.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <ChefHat className="h-5 w-5" />
                  Instrucciones
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {instructions.map((step, index) => (
                    <div key={index} className="flex gap-4">
                      <div className="flex-shrink-0">
                        <div className="w-8 h-8 bg-primary text-white rounded-full flex items-center justify-center text-sm font-medium">
                          {index + 1}
                        </div>
                      </div>
                      <div className="flex-1">
                        <p className="text-gray-700 leading-relaxed">{step}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Ratings Section */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Star className="h-5 w-5" />
                Valoraciones
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {/* Average Rating Display */}
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-1">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <StarIcon
                        key={star}
                        className={cn(
                          "h-5 w-5",
                          star <= Math.round(averageRating)
                            ? "text-yellow-400 fill-current"
                            : "text-gray-300"
                        )}
                      />
                    ))}
                  </div>
                  <span className="text-sm text-gray-600">
                    {averageRating.toFixed(1)} ({totalRatings} valoraciones)
                  </span>
                </div>

                {/* User Rating */}
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-2">Tu valoración:</p>
                  <div className="flex items-center gap-1">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <button
                        key={star}
                        onClick={() => handleRatingClick(star)}
                        className="p-1 hover:scale-110 transition-transform"
                      >
                        <StarIcon
                          className={cn(
                            "h-6 w-6",
                            star <= userRating
                              ? "text-yellow-400 fill-current"
                              : "text-gray-300 hover:text-yellow-300"
                          )}
                        />
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Right Column - Sidebar */}
        <div className="space-y-6">
          {/* Ingredients */}
          {ingredients.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Utensils className="h-5 w-5" />
                  Ingredientes
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {ingredients.map((ingredient, index) => (
                    <div 
                      key={index} 
                      className="flex justify-between items-start p-2 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      <span className="text-gray-700 flex-1">
                        {ingredient.name}
                      </span>
                      {(ingredient.quantity || ingredient.unit) && (
                        <span className="text-sm text-gray-500 font-medium ml-2">
                          {ingredient.quantity && `${ingredient.quantity} `}
                          {ingredient.unit || ''}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Recipe Tags */}
          {recipe.tags && recipe.tags.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Categorías</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {recipe.tags.map((tag) => (
                    <Badge
                      key={tag.id}
                      variant="secondary"
                      className="text-xs"
                    >
                      {tag.tag}
                    </Badge>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Recipe Info */}
          <Card>
            <CardHeader>
              <CardTitle>Información</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3 text-sm">
                {recipe.source && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Fuente:</span>
                    <span className="font-medium">{recipe.source}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-gray-600">Porciones:</span>
                  <span className="font-medium">{recipe.servings}</span>
                </div>
                {recipe.timeMinutes && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Tiempo:</span>
                    <span className="font-medium">{recipe.timeMinutes} min</span>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Meal Planner Modal */}
      <MealPlannerModal
        open={mealPlannerOpen}
        onClose={() => setMealPlannerOpen(false)}
        onConfirm={handleConfirmSchedule}
        recipe={recipe}
      />
    </div>
  );
}
